import React, { useEffect, useContext, useMemo, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Col,
  Row,
  Descriptions,
  Skeleton,
  Divider,
  Tooltip,
  Button,
} from 'antd';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import { changeDocumentTitle } from '@utils/changeDocumentTitle';
import {
  bytesFormat,
  extractDateInISOFormat,
  formatCurrency,
  formatPricePerUnit,
} from '@utils/formatters';
import BackToListing from '@components/BackToListing';
import SessionContext from '@providers/Session/SessionContext';
import Transfers from '@pages/Operational/Transfers';
import Inventory from '@pages/Assets/Inventory';
import CustomCard from '@components/CustomCard';
import CustomTag from '@components/CustomTag';

const DetailedRequest = () => {
  const { t } = useTranslation(['requests', 'attributes']);
  const { t: tMain } = useTranslation('main');
  const {
    selectedApp,
    customer: { customerLogged, customerLoggedName },
  } = useContext(SessionContext);
  const navigate = useNavigate();

  const { requestId } = useParams();
  const { pathname } = useLocation();

  const { data: requestDetails } = useSwr(
    `/service-proxy/broker/virtual-account/${requestId}`,
  );

  const requestView = useMemo(() => {
    if (requestDetails?.status === 'ARCHIVED') {
      return 'filed';
    }
    if (requestDetails?.status === 'ACTIVE') {
      const customerLoggedId = customerLogged?.id;
      const requestCustomerId = requestDetails?.customer?.id;
      if (customerLoggedId && requestCustomerId) {
        if (customerLoggedId === requestCustomerId) {
          return 'pay';
        }
        return 'receive';
      }
    }
    return undefined;
  }, [requestDetails, customerLogged]);

  const contractDetails = useMemo(
    () => requestDetails?.virtualContract,
    [requestDetails],
  );

  const customerDetails = useMemo(
    () => requestDetails?.customer,
    [requestDetails],
  );

  useEffect(() => {
    changeDocumentTitle({
      t: tMain,
      pathname,
      customerLoggedName,
      detailName: customerDetails?.name,
    });
  }, [pathname, customerDetails, customerLoggedName, tMain]);

  const activatedTag = useCallback(
    (value: boolean) => (
      <CustomTag color={value ? 'green' : 'red'}>
        {t(
          `attributes:automaticBlocking.${value ? 'activated' : 'disabled'}`,
        ).toUpperCase()}
      </CustomTag>
    ),
    [t],
  );

  const typeOfPenalty = useMemo(
    () => t(`attributes:typesOfPenalty.${requestDetails?.loyaltyType}`),
    [t, requestDetails],
  );

  const requestItemsDescriptions = useMemo(
    () => [
      {
        label: t('attributes:description'),
        value: requestDetails?.name,
        showItem: requestDetails?.name,
      },
      {
        label: t('attributes:monthlyPayment'),
        value: formatCurrency(parseFloat(requestDetails?.value)),
        showItem: requestDetails?.value,
      },
      {
        label: t('attributes:megabyteExcPrice'),
        value: formatPricePerUnit(requestDetails?.megabyteExcPrice),
        showItem: requestDetails?.megabyteExcPrice,
      },
      {
        label: t('attributes:activationPrice'),
        value: formatCurrency(parseFloat(requestDetails?.activationPrice)),
        showItem: requestDetails?.activationPrice,
      },
      {
        label: t('attributes:typesOfPenalty.title'),
        value: typeOfPenalty,
        showItem: requestDetails?.loyaltyType,
      },
      {
        label: t('attributes:planType'),
        value: t(
          `attributes:planTypes.${requestDetails?.planType?.toLowerCase()}`,
        ),
        showItem: requestDetails?.planType,
      },
      {
        label: t('attributes:planExpirationMonths'),
        value: `${requestDetails?.planExpirationMonths} ${t(
          'attributes:months',
        )}`,
        showItem: requestDetails?.planExpirationMonths,
      },
      {
        label: t('attributes:automaticRenewal'),
        value: requestDetails?.automaticRenewal
          ? t('attributes:hasTag.yes')
          : t('attributes:hasTag.no'),
        showItem: requestDetails?.planType,
      },
      {
        label: t('attributes:iscaDurationInDays'),
        value: requestDetails?.iscaDurationInDays,
        showItem: requestDetails?.planType === 'BAIT',
      },
      {
        label: t('attributes:iscaMaxRenewal'),
        value: requestDetails?.iscaMaxRenewal,
        showItem: requestDetails?.planType === 'BAIT',
      },
      {
        label: t('attributes:penaltyAmount'),
        value: formatCurrency(parseFloat(requestDetails?.penalty)),
        showItem: requestDetails?.penalty && requestDetails?.loyaltyType === 1,
      },
      {
        label: t('attributes:loyaltyTime'),
        value: requestDetails?.loyaltyTime,
        showItem: requestDetails?.loyaltyType === 2,
      },
      {
        label: t('attributes:automaticBlocking.title'),
        value: activatedTag(requestDetails?.automaticBlocking),
        showItem: true,
      },
      {
        label: t('attributes:activationDate'),
        value:
          requestDetails?.activationDate &&
          extractDateInISOFormat(requestDetails?.activationDate),
        showItem: requestDetails?.activationDate,
      },
      {
        label: t('attributes:minimumConsumption'),
        value:
          requestDetails?.preActivationExemptTraffic &&
          bytesFormat(requestDetails?.preActivationExemptTraffic),
        showItem: true,
      },
      {
        label: t('attributes:nbPreActivationDays'),
        value:
          !requestDetails?.nbPreActivationDays ||
          requestDetails?.nbPreActivationDays === 0
            ? t('attributes:noPreActivation')
            : t(
                requestDetails?.nbPreActivationDays > 1
                  ? 'attributes:day_plural'
                  : 'attributes:day',
                {
                  count: requestDetails?.nbPreActivationDays,
                },
              ),
        showItem: true,
      },
      {
        label: t('attributes:numberOfTradeInInvoices'),
        value:
          requestDetails?.tradeInMonths ??
          t('modal.configStep.noTradeInInvoices'),
        showItem: requestDetails?.tradeInMonths,
      },
      {
        label: t('attributes:nbSimcards'),
        value: requestDetails?.nbSimcards,
        showItem: requestDetails?.nbSimcards,
      },
      {
        label: t('attributes:blockedByDefault'),
        value: activatedTag(requestDetails?.blockedByDefault),
        showItem: true,
      },
    ],
    [t, requestDetails, typeOfPenalty, activatedTag],
  );

  const contractTypeFormatted = useMemo(() => {
    if (contractDetails?.payPerUseFlag === 'S') {
      return t('attributes:type.payPerUse');
    }

    switch (contractDetails?.sharingType) {
      case 1:
        return t('attributes:type.sharedWithout');
      case 2:
        return t('attributes:type.sharedTotal');
      case 3:
        return t('attributes:type.sharedMixed');
      default:
        return '';
    }
  }, [contractDetails, t]);

  const contractDescriptionLink = useMemo(
    () =>
      contractDetails?.id ? (
        <Tooltip title={t('view-contract')}>
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => {
              navigate(
                `/${selectedApp}/mobile-network/financial/contracts/${contractDetails?.id}`,
              );
            }}
          >
            <span>{contractDetails?.planDesc}</span>
          </Button>
        </Tooltip>
      ) : (
        contractDetails?.planDesc
      ),
    [contractDetails, navigate, t, selectedApp],
  );

  const contractItemsDescriptions = useMemo(
    () => [
      {
        label: t('attributes:description'),
        value: contractDescriptionLink,
        showItem: contractDetails?.planDesc,
      },
      {
        label: t('attributes:externalId'),
        value: contractDetails?.externalId,
        showItem: contractDetails?.externalId,
      },
      {
        label: t('attributes:type.title'),
        value: contractTypeFormatted,
        showItem: contractTypeFormatted,
      },
      {
        label: t('attributes:franchise'),
        value: bytesFormat(contractDetails?.franchiseBytes),
        showItem: contractDetails?.franchiseBytes,
      },
      {
        label: t('attributes:vendor'),
        value: contractDetails?.motOperators?.name,
        showItem: contractDetails?.motOperators?.name,
      },
    ],
    [t, contractDescriptionLink, contractTypeFormatted, contractDetails],
  );

  const clientItemsDescriptions = useMemo(
    () => [
      {
        label: t('attributes:name'),
        value: customerDetails?.name,
      },
      {
        label: t('attributes:nickname'),
        value: customerDetails?.nickname,
      },
      {
        label: t('attributes:cpfCnpj'),
        value: customerDetails?.cpfCnpj,
      },
      {
        label: t('attributes:tradingName'),
        value: customerDetails?.tradingName,
      },
      {
        label: t('attributes:customerType.title'),
        value: t(`attributes:customerType.${customerDetails?.type}`),
      },
    ],
    [t, customerDetails],
  );

  const DividerModel = useCallback(
    ({ title }: any) => (
      <Divider
        style={{
          marginTop: 24,
          marginBottom: 32,
          fontSize: '0.85rem',
          opacity: 0.7,
          textTransform: 'uppercase',
        }}
        orientation="left"
      >
        {title}
      </Divider>
    ),
    [],
  );

  return (
    <Skeleton loading={!requestDetails} active>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col lg={24} sm={24} xs={24}>
          <CustomCard
            title={
              <BackToListing
                style={{ margin: 0 }}
                title={t('requestDetails')}
              />
            }
          >
            <Descriptions
              column={{ lg: 3, md: 2, sm: 2, xs: 1 }}
              style={{ marginTop: 24, padding: 24 }}
            >
              {requestItemsDescriptions?.map(
                ({ label, value, showItem }) =>
                  showItem &&
                  value && (
                    <Descriptions.Item key={label} label={label}>
                      {value}
                    </Descriptions.Item>
                  ),
              )}
            </Descriptions>
            <DividerModel title={t('attributes:contract')} />
            <Descriptions
              column={{ lg: 3, md: 2, sm: 2, xs: 1 }}
              style={{ padding: 24 }}
            >
              {contractItemsDescriptions?.map(
                ({ label, value, showItem }) =>
                  showItem &&
                  value && (
                    <Descriptions.Item key={label} label={label}>
                      {value}
                    </Descriptions.Item>
                  ),
              )}
            </Descriptions>

            <DividerModel title={t('attributes:customer')} />
            <Descriptions
              column={{ lg: 3, md: 2, sm: 2, xs: 1 }}
              style={{ padding: 24 }}
            >
              {clientItemsDescriptions?.map(
                ({ label, value }) =>
                  value && (
                    <Descriptions.Item key={label} label={label}>
                      {value}
                    </Descriptions.Item>
                  ),
              )}
            </Descriptions>
          </CustomCard>
        </Col>
      </Row>

      {requestDetails?.id && requestView && requestView !== 'pay' && (
        <div style={{ marginTop: 16 }}>
          <Transfers
            isInTheRequestDetails
            defaultFilters={{
              virtualAccountId: requestDetails?.id,
            }}
            columnsToRemove={['oldCustomerName', 'newCustomerName', 'request']}
          />
        </div>
      )}

      {requestDetails?.id && requestView && requestView !== 'filed' && (
        <div style={{ marginTop: 16 }}>
          <Inventory
            tableOnly
            defaultFilters={{
              accountId: requestDetails?.id,
              showContracted: 'false',
            }}
            defaultColumns={[
              'actions',
              'consumption',
              'operator',
              'connOperator',
              'rat',
              'online',
              'blockingType',
              'msisdn',
              'iccid',
              'imsi',
              'imei',
              'lastConnection',
              'status',
              'activationDate',
              'imeiLocked',
            ]}
          />
        </div>
      )}
    </Skeleton>
  );
};

export default DetailedRequest;
